<template>
  <section class="form-button-toggle">
    <ValidationProvider
      ref="validationProvider"
      :rules="rules"
      :name="formattedLabel"
      :value="computedValue"
    >
      <div class="is-flex is-align-items-center">
        <label v-if="formattedLabel" class="mr-3" :data-e2e="FormCypressAttribute.views.LABEL">
          {{ formattedLabel }}
        </label>
        <div class="buttons has-addons">
          <section v-for="(value, index) in values" :key="index">
            <span
              v-if="showOrLabel && index === values.length - 1"
              class="is-inline-flex px-4 m-t-7 or-label has-text-grey-light has-text-weight-semibold"
              :data-e2e="FormCypressAttribute.views.OR_LABEL"
            >
              {{ $t('asset.or') }}
            </span>
            <button
              class="button has-text-primary has-text-weight-semibold is-borderless is-full-height m-b-0 is-selectable"
              :disabled="readOnly && !isItemSelected(value)"
              :class="{ 'is-selected': isItemSelected(value) }"
              @click.prevent.stop="onSelect(value)"
              :data-e2e="FormCypressAttribute.inputs.TOGGLE"
            >
              <slot name="button" :value="value">
                {{ computedValues(value) }}
              </slot>
            </button>
          </section>
        </div>
      </div>
    </ValidationProvider>
  </section>
</template>

<script>
import { FormInputMixin } from '/@/modules/core/form/mixins/FormInputMixin';
import { FormComputedValueMixin } from '/@/modules/core/form/mixins/FormComputedValueMixin';
import ValidationProvider from '/@/modules/core/form/components/ValidationProvider.vue';
import { FormCypressAttribute } from '/@/modules/core/form/constants/FormCypressAttribute';
import { FormValidationMixin } from '/@/modules/core/form/mixins/FormValidationMixin';
import { toRaw } from 'vue';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'FormButtonToggle',
  emits: ['select'],
  slotsDefinition: {
    button: { value: Object }
  },
  mixins: [FormInputMixin, FormComputedValueMixin, FormValidationMixin],
  components: { ValidationProvider },
  props: {
    values: {
      required: true,
      type: Array
    },
    valueByProp: {
      default: undefined,
      type: String
    },
    showOrLabel: {
      type: Boolean,
      default: false
    }
  },
  static: {
    FormCypressAttribute
  },
  data() {
    return {
      selectedItem: null
    };
  },
  watch: {
    value(newValue) {
      this.$refs.validationProvider.validate(newValue);
    }
  },
  methods: {
    computedValues(value) {
      return this.valueByProp ? value[this.valueByProp] : value;
    },
    emitValue(value) {
      return this.valueByProp ? value.key : value;
    },
    isItemSelected(item) {
      const rawValue = toRaw(this.value ?? this.computedValue);
      if (this.valueByProp) {
        return rawValue === this.emitValue(item);
      }
      return rawValue === item;
    },
    onSelect(item) {
      const value = this.emitValue(item);
      this.computedValue = value;
      this.$emit('select', value);
    }
  },
  mounted() {
    this.$refs.validationProvider.validate(this.computedValue);
  }
};
</script>

<style scoped>
.buttons {
  background-color: var(--ce-white-ter);
  border-radius: var(--ce-radius-large);
  flex-wrap: nowrap;
}

button {
  background: var(--ce-white-ter);
  border: solid var(--ce-2px) transparent !important;
  transition: background-color 0.3s ease;
  text-decoration: none;
  padding: var(--ce-6px) var(--ce-10px);
  box-shadow: none;
  border-radius: var(--ce-radius-large) !important;

  &:hover {
    box-shadow: none;
    border: solid var(--ce-2px) var(--ce-primary) !important;
  }
  &.is-selected {
    pointer-events: none;
    background: var(--ce-primary-light-30);
  }

  &:disabled {
    border: none !important;
    background: var(--ce-white-ter);
    color: var(--ce-grey);
  }
}

.or-label {
  background: transparent;
  border: solid var(--ce-2px) transparent !important;
}
</style>
